import React from 'react';
import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import PropTypes from 'prop-types';
import Utils from '../utils';
import UI from '../ui';
import Util from './Util';

const { WrappedCheckbox, WrappedTooltip } = UI;
const { Session } = Utils;

// Return className string
const getStoreClassName = name => !!name && name.toLowerCase().replace(/\s+/g, '');

// Return `true` if store is checked
const isStoreChecked = (id, stores) =>
	stores && stores.some(store => store.storeId === id.toString() && store.included);

const isOtherAndManualChecked = (otherPrints, stores) =>
	otherPrints && (stores && stores.length === 1 ? stores[0].included : true);

const cbAllOtherValue = 'AllOther';
const cbOtherAndManualValue = 'OtherAndManual';

const StoresTabPanel = ({ reference, storesList, setReference }) => {
	const { otherPrints, stores } = isObject(reference.enabledPrints)
		? reference.enabledPrints
		: {};
	const { brandName } = Session.getBrand();

	const onCheckboxChange = e => {
		const { checked, value } = e.target;

		if (value === cbOtherAndManualValue) {
			if (isArray(stores) && stores.length === 1) {
				stores[0].included = checked;
			}
		}
		setReference({
			...reference,
			enabledPrints: {
				...reference.enabledPrints,

				// update `otherPrints` when user checks or unchecks 'All Other Stores' checkbox
				otherPrints:
					value === cbAllOtherValue || value === cbOtherAndManualValue
						? checked
						: reference.enabledPrints.otherPrints,

				// update `stores` when user checks (or unchecks) any checkbox but 'All Other Stores'
				stores:
					value !== cbAllOtherValue && value !== cbOtherAndManualValue
						? stores.map(store =>
								store.storeId === `${value}`
									? {
											...store,
											included: checked,
									  }
									: store
						  )
						: stores,
			},
		});
	};

	const isNoStoreConnected = Util.isNoStoreConnected(storesList);

	return (
		<div className="branded-tracking-stores-tab-panel">
			{!isNoStoreConnected && (
				<>
					<section className="header-section">
						Select the Stores for which you'd like Branded Tracking to apply:
					</section>
					<section>
						<ul>
							{storesList.length > 0 &&
								storesList.map(store => (
									<li
										key={store.StoreID}
										className={`store-list-item ${
											Util.isStoreDisabled(store) ? 'disabled-list' : ''
										}`}
									>
										<WrappedCheckbox
											inputId={`cb${store.StoreID}`}
											name="brandedTrackingStores"
											value={store.StoreID}
											onChange={onCheckboxChange}
											checked={isStoreChecked(store.StoreID, stores)}
											htmlFor={`cb${store.StoreID}`}
											dataqa={`brandedTrackingStores${getStoreClassName(
												store.Marketplace.Name
											)}`}
											disabled={Util.isStoreDisabled(store)}
										>
											<div
												className={`common-marketplaces marketplace-icon-${getStoreClassName(
													store.Marketplace.Name
												)}`}
											/>
											{!Util.isStoreDisabled(store) && <>{store.StoreName}</>}
											{Util.isStoreDisabled(store) && (
												<>
													<div className="store-name-text">
														{store.StoreName} (unavailable for this
														store)
													</div>
													<WrappedTooltip
														target="store-name-text"
														position="bottom"
														className="store-disabled-tooltip"
														tooltip="Branded Tracking is not available for Amazon stores, as Amazon requires that sellers use its own solutions."
													/>
												</>
											)}
										</WrappedCheckbox>
									</li>
								))}
							<li>
								<WrappedCheckbox
									inputId="cbAllOther"
									name="brandedTrackingStores"
									value={cbAllOtherValue}
									onChange={onCheckboxChange}
									checked={otherPrints}
									htmlFor="cbAllOther"
									dataqa="brandedTrackingAllOtherStores"
								>
									<div className="common-marketplaces marketplace-icon-shipstation" />
									All Other Labels
								</WrappedCheckbox>
							</li>
						</ul>
					</section>
					<section className="description-section">
						Any unchecked stores will use the default {brandName} tracking page.
					</section>
				</>
			)}

			{isNoStoreConnected && (
				<>
					<section>
						<ul>
							<li>
								<WrappedCheckbox
									inputId="cbOtherAndManual"
									name="brandedTrackingStores"
									value={cbOtherAndManualValue}
									onChange={onCheckboxChange}
									checked={isOtherAndManualChecked(otherPrints, stores)}
									htmlFor="cbOtherAndManual"
									dataqa="brandedTrackingAllOtherStoresAndManual"
								>
									Use Branded Tracking for all shipping label prints
								</WrappedCheckbox>
							</li>
						</ul>
					</section>
					<section className="description-section">
						Unchecking this box will direct your tracking links to the default{' '}
						{brandName} tracking page.
					</section>
				</>
			)}
		</div>
	);
};

StoresTabPanel.propTypes = {
	reference: PropTypes.object.isRequired,
	storesList: PropTypes.array.isRequired,
	setReference: PropTypes.func.isRequired,
};

export default StoresTabPanel;
